import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'sushiweb/Modal/Modal';
import {
  Container,
  Header,
  HeaderGradient,
  HeaderLogo,
  HeaderTitle,
  Content,
  Subtitle,
  SectionTitle,
  OptionsContainer,
  OptionCard,
  RadioCircle,
  RadioInner,
  OptionLabel,
  Footer,
  OptionContent,
  OptionLogoContainer,
  OptionLogo,
  MultipleLogosContainer,
  ModalContent,
  ModalText,
  ModalButtonContainer,
  PrimaryButton,
  ModalTitle,
} from './styles';
import {
  B_LOGO,
  BISTRO_LOGO_WHITE,
  D_LOGO_WHITE,
  E_LOGO_WHITE,
  Z_LOGO_WHITE,
} from '../../constants/images';
import { entityTypeEnum } from '../common/components/constants';
import { getEntityColor } from '../../helpers';

const GiftCardEntitySelection = ({ onGenerateCode, gcData }) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const { Amount = '0' } = gcData;

  const options = [
    {
      id: entityTypeEnum.BLINKIT_USER,
      label: 'Blinkit & Bistro',
      logo: [B_LOGO, BISTRO_LOGO_WHITE],
      bgColor: [getEntityColor(entityTypeEnum.BLINKIT_USER), '#00DD6E'],
    },
    {
      id: entityTypeEnum.DISTRICT_USER,
      label: 'District',
      logo: [D_LOGO_WHITE],
      bgColor: [getEntityColor(entityTypeEnum.DISTRICT_USER)],
    },
    {
      id: entityTypeEnum.ZOMATO_USER,
      label: 'Zomato',
      logo: [Z_LOGO_WHITE],
      bgColor: [getEntityColor(entityTypeEnum.ZOMATO_USER)],
    },
  ];

  const renderLogos = (logos, bgColors, label) => {
    if (logos?.length === 1) {
      return (
        <OptionLogoContainer bgColor={bgColors[0]}>
          <OptionLogo src={logos[0]} alt={`${label} logo`} />
        </OptionLogoContainer>
      );
    }

    return (
      <MultipleLogosContainer>
        {logos?.map((logo, index) => (
          <OptionLogoContainer
            bgColor={bgColors[index] || bgColors[0]}
            isMultiple
            logoIndex={index}
          >
            <OptionLogo
              src={logo}
              alt={`${label} logo ${index + 1}`}
              isMultiple
            />
          </OptionLogoContainer>
        ))}
      </MultipleLogosContainer>
    );
  };

  const handleButtonClick = () => {
    if (selectedOption) {
      setShowWarningModal(true);
    }
  };

  const handleConfirmGenerate = () => {
    setShowWarningModal(false);
    if (onGenerateCode) {
      onGenerateCode(selectedOption);
    }
  };

  const handleCancelGenerate = () => {
    setShowWarningModal(false);
  };

  const getSelectedOptionName = () => {
    const option = options.find(opt => opt?.id === selectedOption);
    return option ? option?.label : '';
  };

  return (
    <Container>
      <Header>
        <HeaderGradient>
          <HeaderLogo src={E_LOGO_WHITE} alt='Eternal Logo' />
          <HeaderTitle>Happy Diwali from all of us at Eternal</HeaderTitle>
        </HeaderGradient>
      </Header>

      <Content>
        <Subtitle>
          Here&apos;s ₹
          {Amount}
          {' '}
          voucher to add some sparkle to your
          celebrations!
        </Subtitle>

        <SectionTitle>
          Pick where you&apos;d like to redeem (choose one):
        </SectionTitle>

        <OptionsContainer>
          {options.map(option => (
            <OptionCard
              key={option.id}
              selected={selectedOption === option.id}
              onClick={() => setSelectedOption(option.id)}
            >
              <OptionContent>
                <RadioCircle selected={selectedOption === option.id}>
                  {selectedOption === option.id && <RadioInner />}
                </RadioCircle>
                <OptionLabel>{option.label}</OptionLabel>
              </OptionContent>
              {renderLogos(option.logo, option.bgColor, option.label)}
            </OptionCard>
          ))}
        </OptionsContainer>
        <SectionTitle>
          You can redeem this voucher on only one of the above platforms. Once
          redeemed, it cannot be used on any other platform.
        </SectionTitle>
      </Content>

      <Footer>
        <PrimaryButton
          onClick={handleButtonClick}
          disabled={!selectedOption}
          fullWidth
        >
          Continue
        </PrimaryButton>
      </Footer>

      <Modal
        visible={showWarningModal}
        type='medium'
        isCentered
        padding='2rem'
        onClose={handleCancelGenerate}
        showCloseIcon
        title=''
        titleComponent={<ModalTitle> </ModalTitle>}
        borderRadius='8px'
      >
        <ModalContent>
          <ModalText>
            You have chosen 
            {' '}
            <strong>{getSelectedOptionName()}</strong>
            {' '}
            for your
            ₹
            {Amount}
            {' '}
            Diwali voucher.
          </ModalText>
          <ModalText>
            <strong>Note:</strong>
            {' '}
            Once generated, this voucher can only be used
            on
            {getSelectedOptionName()}
            {' '}
            and
            <strong>cannot</strong>
            {' '}
            be used on
            any other platform.
          </ModalText>

          <ModalButtonContainer>
            <PrimaryButton onClick={handleConfirmGenerate} size='medium'>
              Continue
            </PrimaryButton>
          </ModalButtonContainer>
        </ModalContent>
      </Modal>
    </Container>
  );
};

GiftCardEntitySelection.propTypes = {
  onGenerateCode: PropTypes.func,
  gcData: PropTypes.shape({
    Amount: PropTypes.string,
  }),
};

GiftCardEntitySelection.defaultProps = {
  onGenerateCode: () => {},
  gcData: {
    Amount: '0',
  },
};

export default GiftCardEntitySelection;
